/**
 * Feature 073 — Owner-facing list of attached PDFs with edit/delete actions
 * and view/download counters. Used inside ProviderDocumentsManager.
 */

import { FileText, Pencil, Trash2 } from 'lucide-react';
import type { OwnerProviderDocument } from '../../types/provider-profile.types';

const formatBytes = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

type ProviderDocumentListProps = {
  documents: OwnerProviderDocument[];
  isLoading: boolean;
  onEdit: (doc: OwnerProviderDocument) => void;
  onDelete: (doc: OwnerProviderDocument) => void;
};

export function ProviderDocumentList({
  documents,
  isLoading,
  onEdit,
  onDelete,
}: ProviderDocumentListProps) {
  return (
    <div>
      <h4 className="font-medium text-foreground">Existing documents</h4>
      {isLoading ? (
        <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
      ) : documents.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          No PDFs attached yet.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {documents.map((doc) => (
            <li
              key={doc.id}
              data-testid={`provider-document-row-${doc.id}`}
              className="flex items-start justify-between gap-3 rounded-md border border-border bg-card p-3"
            >
              <div className="flex min-w-0 gap-3">
                <FileText className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">
                    {doc.displayName}
                  </p>
                  {doc.description ? (
                    <p className="text-sm text-muted-foreground">
                      {doc.description}
                    </p>
                  ) : null}
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {formatBytes(doc.fileSize)} · {doc.viewCount} view
                    {doc.viewCount === 1 ? '' : 's'} · {doc.downloadCount}{' '}
                    download{doc.downloadCount === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              <div className="flex flex-shrink-0 gap-1.5">
                <button
                  type="button"
                  aria-label={`Edit ${doc.displayName}`}
                  onClick={() => onEdit(doc)}
                  className="rounded-md border border-border p-1.5 hover:bg-muted"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${doc.displayName}`}
                  onClick={() => onDelete(doc)}
                  className="rounded-md border border-border p-1.5 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
